// @ts-nocheck
// Motor de áudio — gravação e reprodução.
//
// Isto nasceu enterrado dentro de `book-study.tsx`, sem exportar nada, e é a
// coisa mais valiosa daquela tela: um gravador que sabe negociar codec por
// plataforma, contornar o pedido de permissão do Capacitor no Android, extrair
// a forma de onda REAL do que foi gravado, e um player com scrub por arraste e
// coordenação global de "um áudio por vez".
//
// Nada aqui sabe o que é um livro. Sai daqui inteiro para o Estudo do clube.

import React, { useCallback, useEffect, useRef, useState } from 'react';

export const DEFAULT_WAVE = [0.4, 0.7, 0.55, 0.9, 0.6, 0.35, 0.8, 0.5, 1, 0.65, 0.45, 0.85, 0.6, 0.3, 0.75, 0.55, 0.95, 0.5, 0.7, 0.4, 0.6, 0.8];

export function fmtTime(sec: number) {
  sec = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(sec / 60), s = sec % 60;
  return m + ':' + (s < 10 ? '0' + s : s);
}

// Escolhe um mimeType de gravação suportado (iOS → mp4/aac, Chrome/Android → webm/opus)
export function pickAudioMime() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  for (const c of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/mpeg', 'audio/ogg']) {
    try { if (MediaRecorder.isTypeSupported(c)) return c; } catch (_) {}
  }
  return '';
}

export function extForMime(mime: string) {
  const b = String(mime || '').toLowerCase().split(';')[0].trim();
  if (b.includes('mp4') || b.includes('aac')) return 'm4a';
  if (b.includes('mpeg')) return 'mp3';
  if (b.includes('ogg')) return 'ogg';
  return 'webm';
}

// Extrai ~28 picos reais do áudio gravado (fallback: padrão default)
export async function computePeaks(blob: Blob, n = 28): Promise<number[] | null> {
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return null;
    const ctx = new Ctx();
    const arr = await blob.arrayBuffer();
    const audio: AudioBuffer = await ctx.decodeAudioData(arr);
    const data = audio.getChannelData(0);
    const block = Math.floor(data.length / n) || 1;
    const peaks: number[] = []; let max = 0.0001;
    for (let i = 0; i < n; i++) {
      let sum = 0;
      for (let j = 0; j < block; j++) sum += Math.abs(data[i * block + j] || 0);
      const avg = sum / block; peaks.push(avg); if (avg > max) max = avg;
    }
    try { ctx.close(); } catch (_) {}
    return peaks.map(p => Math.max(0.12, Math.min(1, p / max)));
  } catch (_) { return null; }
}

const playSvg = <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M7 5.5v13a1 1 0 0 0 1.5.87l11-6.5a1 1 0 0 0 0-1.74l-11-6.5A1 1 0 0 0 7 5.5z" /></svg>;
const pauseSvg = <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1.3" /><rect x="14" y="5" width="4" height="14" rx="1.3" /></svg>;
const spinSvg = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" style={{ animation: 'bsSpin .7s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.2-8.5" /></svg>;

// Coordenação: só um áudio toca por vez na página. É variável de módulo de
// propósito — com dezenas de players num feed, cada um checando os outros seria
// O(n²); um ponteiro só resolve.
let _currentAudio: HTMLAudioElement | null = null;

export function AudioPlayer({ url, dur, wave, compact }: { url: string; dur: number; wave?: number[] | null; compact?: boolean }) {
  const ref = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [realDur, setRealDur] = useState(dur || 0);
  const [loading, setLoading] = useState(false);
  const barsRef = useRef<HTMLDivElement>(null);
  const scrubbingRef = useRef(false);
  const bars = (wave && wave.length ? wave : DEFAULT_WAVE);
  const d = realDur || dur || 1;
  const pct = Math.min(1, elapsed / d);

  useEffect(() => () => { if (_currentAudio === ref.current) _currentAudio = null; }, []);

  const toggle = () => {
    const a = ref.current; if (!a) return;
    if (playing) { a.pause(); return; }
    if (_currentAudio && _currentAudio !== a) { try { _currentAudio.pause(); } catch (_) {} }
    _currentAudio = a; a.playbackRate = speed;
    if (a.readyState < 2) setLoading(true); // ainda não tem dados → mostra carregando
    const p = a.play();
    if (p && p.then) p.then(() => setLoading(false)).catch(() => {
      // 1ª tentativa falhou (não carregou ainda) → espera ficar pronto e toca sozinho
      const onReady = () => { a.removeEventListener('canplay', onReady); a.play().then(() => setLoading(false)).catch(() => setLoading(false)); };
      a.addEventListener('canplay', onReady);
      try { a.load(); } catch (_) {}
    });
  };
  const cycleSpeed = () => { const next = speed === 1 ? 1.5 : (speed === 1.5 ? 2 : 1); setSpeed(next); if (ref.current) ref.current.playbackRate = next; };
  // navegação por arraste (estilo WhatsApp): toca/segura na onda e arrasta pra frente/trás
  const scrubTo = (clientX: number) => {
    const el = barsRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    let f = (clientX - r.left) / r.width; f = Math.max(0, Math.min(1, f));
    const t = f * d;
    const a = ref.current; if (a) { try { a.currentTime = t; } catch (_) {} }
    setElapsed(t);
  };
  const onScrubDown = (e: any) => {
    scrubbingRef.current = true;
    scrubTo(e.touches ? e.touches[0].clientX : e.clientX);
    const move = (ev: any) => { if (ev.cancelable) ev.preventDefault(); scrubTo(ev.touches ? ev.touches[0].clientX : ev.clientX); };
    const up = () => { scrubbingRef.current = false; window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); window.removeEventListener('touchmove', move); window.removeEventListener('touchend', up); window.removeEventListener('touchcancel', up); };
    window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: false }); window.addEventListener('touchend', up); window.addEventListener('touchcancel', up);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 12, padding: compact ? '6px 8px' : '8px 10px' }}>
      {/* `metadata`, não `auto`: num feed com centenas de notas de voz, `auto`
          baixaria todos os arquivos inteiros na primeira rolagem. E não pode ser
          `none` — no iOS o play() precisa sair do gesto do dedo, e um elemento
          que ainda não tocou em rede rejeita a promessa e cai no retry, que já
          está fora do gesto e é bloqueado. */}
      <audio ref={ref} src={url} preload="metadata"
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onPlaying={() => { setPlaying(true); setLoading(false); }}
        onWaiting={() => setLoading(true)}
        onCanPlay={() => setLoading(false)}
        onEnded={() => { setPlaying(false); setLoading(false); setElapsed(0); }}
        onError={() => setLoading(false)}
        onTimeUpdate={() => { if (ref.current && !scrubbingRef.current) setElapsed(ref.current.currentTime); }}
        onLoadedMetadata={() => { const dd = ref.current?.duration; if (dd && isFinite(dd) && dd > 0) setRealDur(dd); }} />
      <button onClick={toggle} aria-label={loading ? 'Carregando' : (playing ? 'Pausar' : 'Tocar')} style={{ width: compact ? 32 : 38, height: compact ? 32 : 38, flex: 'none', borderRadius: '50%', border: 'none', background: 'var(--ember)', color: '#1c1208', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        {loading ? spinSvg : (playing ? pauseSvg : playSvg)}
      </button>
      <div ref={barsRef} onMouseDown={onScrubDown} onTouchStart={onScrubDown} style={{ flex: 1, minWidth: 0, position: 'relative', height: compact ? 24 : 30, cursor: 'pointer', touchAction: 'pan-y' }}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
          {bars.map((h, i) => (<div key={i} style={{ flex: 1, height: Math.round(25 + h * 75) + '%', background: ((i + 1) / bars.length) <= pct ? 'var(--ember)' : 'var(--ink-4)', borderRadius: 2, minWidth: 2 }} />))}
        </div>
        {/* thumb arrastável (estilo WhatsApp) */}
        <div style={{ position: 'absolute', top: '50%', left: `${pct * 100}%`, width: 13, height: 13, marginLeft: -6.5, marginTop: -6.5, borderRadius: '50%', background: '#fff', border: '2px solid var(--ember)', boxShadow: '0 1px 4px rgba(0,0,0,0.35)', pointerEvents: 'none', transition: scrubbingRef.current ? 'none' : 'left .08s linear' }} />
      </div>
      <button onClick={cycleSpeed} aria-label="Velocidade" style={{ flex: 'none', fontFamily: 'Geist Mono, monospace', fontSize: 9.5, color: 'var(--ink-2)', background: 'var(--paper-3)', border: '1px solid var(--line-strong)', borderRadius: 20, padding: '4px 7px', cursor: 'pointer' }}>
        {speed === 1 ? '1×' : (speed === 1.5 ? '1,5×' : '2×')}
      </button>
      <span style={{ flex: 'none', fontFamily: 'Geist Mono, monospace', fontSize: 10.5, color: 'var(--ink-3)', minWidth: 28, textAlign: 'right' }}>
        {(playing || elapsed > 0.05) ? fmtTime(elapsed) : fmtTime(d)}
      </span>
    </div>
  );
}

/* ── O gravador ────────────────────────────────────────────────────────────
   Devolve estado + comandos. Quem chama não precisa saber de MediaRecorder,
   de stream, de timer nem de qual codec o aparelho aceita.

   `onErro` recebe o diagnóstico bruto: a tela do livro manda pro /api/debug-log
   com a versão nativa do app, e quem quiser pode só ignorar.                */
export const MAX_REC_SEC = 600; // 10 minutos

export function useVoiceRecorder(onErro?: (info: { name: string; message: string }) => void) {
  const [stage, setStage] = useState<'idle' | 'recording' | 'recorded'>('idle');
  const [sec, setSec] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [dur, setDur] = useState(0);
  const [wave, setWave] = useState<number[] | null>(null);

  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<any>(null);
  const mimeRef = useRef<string>('');
  const secRef = useRef(0);
  const discardRef = useRef(false);   // true = parada intencional, ignora o onstop
  const urlRef = useRef<string | null>(null);

  useEffect(() => { urlRef.current = url; }, [url]);

  const stop = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { mediaRef.current?.state !== 'inactive' && mediaRef.current?.stop(); } catch (_) {}
  }, []);

  // Descarta tudo: para o stream, libera o objectURL e volta pro zero. É o que
  // roda ao fechar o composer — sem isto o microfone fica aceso.
  const reset = useCallback(() => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    discardRef.current = true;
    try { if (mediaRef.current && mediaRef.current.state !== 'inactive') mediaRef.current.stop(); } catch (_) {}
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (_) {}
    streamRef.current = null;
    setBlob(null); setUrl(null); setDur(0); setWave(null); setSec(0); setErr(null); setStage('idle');
  }, []);

  useEffect(() => () => { reset(); }, [reset]);

  const start = useCallback(async () => {
    setErr(null);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) throw new Error('sem-suporte');
      // Capacitor Android: a 1ª chamada DISPARA o pedido de permissão e rejeita; depois de
      // conceder, a 2ª tentativa já pega o microfone liberado. Re-tenta 1x só em erro de permissão.
      // ESPERAR A PESSOA LER O DIALOGO, e nao um tempo fixo.
      //
      // No Capacitor Android a 1a chamada dispara o pedido de permissao E
      // rejeita; so a 2a, depois de conceder, pega o microfone. A versao
      // anterior esperava 700ms e tentava UMA vez: quem levasse mais que isso
      // para ler o dialogo — o normal na primeira vez — recebia "ative nas
      // configuracoes do app" e ia mexer em ajuste que nao estava errado.
      //
      // Agora sao varias tentativas espacadas, cobrindo ~8s de leitura. Enquanto
      // o dialogo estiver aberto a rejeicao se repete; no instante em que a
      // pessoa toca "Permitir", a proxima tentativa passa. Negar de verdade
      // tambem continua rejeitando, e ai a mensagem aparece — so que depois de
      // ter dado tempo de responder.
      let stream: MediaStream | null = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (permErr: any) {
        const pn = permErr?.name || '';
        if (pn !== 'NotAllowedError' && pn !== 'SecurityError' && pn !== 'AbortError') throw permErr;
        let ultimo: any = permErr;
        for (const espera of [400, 700, 1000, 1500, 2000, 2500]) {
          await new Promise(r => setTimeout(r, espera));
          try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); break; }
          catch (e: any) { ultimo = e; }
        }
        if (!stream) throw ultimo;
      }
      streamRef.current = stream;
      const mime = pickAudioMime(); mimeRef.current = mime;
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      discardRef.current = false;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (_) {}
        if (discardRef.current) { discardRef.current = false; chunksRef.current = []; return; } // descarte intencional
        const type = mimeRef.current || (chunksRef.current[0]?.type) || 'audio/webm';
        const b = new Blob(chunksRef.current, { type });
        const d = secRef.current;
        const u = URL.createObjectURL(b);
        setBlob(b); setUrl(u); setDur(Math.max(1, d)); setStage('recorded');
        const peaks = await computePeaks(b); if (peaks) setWave(peaks);
      };
      mediaRef.current = rec;
      rec.start();
      setStage('recording'); setSec(0); secRef.current = 0;
      timerRef.current = setInterval(() => {
        secRef.current = Math.min(MAX_REC_SEC, secRef.current + 1);
        setSec(secRef.current);
        if (secRef.current >= MAX_REC_SEC) stop();
      }, 1000);
    } catch (e: any) {
      try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (_) {}
      streamRef.current = null; setStage('idle');
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      const name = e?.name || '';
      if (onErro) { try { onErro({ name, message: String(e?.message || e).slice(0, 200) }); } catch (_) {} }
      setErr(
        e?.message === 'sem-suporte' ? 'Seu aparelho não permite gravar áudio aqui ainda.'
        : name === 'NotAllowedError' ? 'Preciso da permissão de microfone. Ative nas configurações do app e tente de novo.'
        : name === 'NotFoundError' ? 'Não encontrei um microfone neste aparelho.'
        : name === 'NotReadableError' ? 'O microfone está sendo usado por outro app. Feche-o e tente de novo.'
        : 'Não consegui acessar o microfone agora. Tente de novo — se persistir, use Texto ou Foto.');
    }
  }, [onErro, stop]);

  // Apaga só a gravação, mantendo o gravador pronto para outra.
  const regravar = useCallback(() => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    setBlob(null); setUrl(null); setDur(0); setWave(null); setStage('idle'); setSec(0);
  }, []);

  // Nome de arquivo com a extensão certa para o codec que ESTE aparelho gravou.
  const nomeDoArquivo = useCallback((base = 'nota') => base + '.' + extForMime(mimeRef.current || (blob && blob.type) || ''), [blob]);

  return { stage, sec, err, blob, url, dur, wave, start, stop, reset, regravar, nomeDoArquivo, setStage };
}
